import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LEAD_STATUSES, leadStatusBadgeClass } from "@/lib/leadStatuses";

export const TeamOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        (async () => { try { const { data } = await api.get("/team/orders"); setOrders(data.orders); } catch {} setLoading(false); })();
    }, []);
    return (
        <div className="p-6 md:p-8" data-testid="team-orders">
            <h1 className="font-display text-3xl font-bold tracking-tight">Orders</h1>
            <p className="text-sm text-muted-foreground">Read-only view of all customer orders</p>
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mt-6" /> : (
                <div className="mt-6 rounded-2xl bg-card border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left"><tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Stage</th></tr></thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => setSelected(o)} data-testid={`team-order-${o.order_number}`}>
                                    <td className="p-3 font-mono text-xs">{o.order_number}</td>
                                    <td className="p-3">{o.address?.full_name}<div className="text-xs text-muted-foreground">{o.address?.phone}</div></td>
                                    <td className="p-3">{formatINR(o.total_inr)}</td>
                                    <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"}`}>{o.payment_status}</span></td>
                                    <td className="p-3 text-xs">{o.status.replace(/_/g, " ")}</td>
                                </tr>
                            ))}
                            {orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No orders.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto">
                    {selected && (<><DialogHeader><DialogTitle>Order {selected.order_number}</DialogTitle></DialogHeader>
                        <div className="text-sm space-y-2">
                            <div>{selected.address?.full_name} · {selected.address?.phone}</div>
                            <div className="text-xs text-muted-foreground">{selected.address?.line1}, {selected.address?.city} {selected.address?.pincode}</div>
                            {selected.items.map((it, i) => (
                                <div key={i} className="rounded-2xl bg-muted/40 p-3">
                                    <div className="font-semibold">{it.product_name}</div>
                                    <div className="text-xs">{it.size_label} · Qty {it.quantity}</div>
                                    {it.notes && <div className="text-xs text-muted-foreground mt-1">Notes: {it.notes}</div>}
                                </div>
                            ))}
                        </div></>)}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export const TeamProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => { try { const { data } = await api.get("/team/products"); setProducts(data.products); } catch {} setLoading(false); })();
    }, []);
    return (
        <div className="p-6 md:p-8" data-testid="team-products">
            <h1 className="font-display text-3xl font-bold tracking-tight">Products</h1>
            <p className="text-sm text-muted-foreground">Read-only catalog</p>
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mt-6" /> : (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {products.map((p) => (
                        <div key={p.id} className="rounded-2xl bg-card border overflow-hidden">
                            <div className="aspect-[4/5] bg-brand-cream overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                            <div className="p-3">
                                <div className="font-semibold text-sm">{p.name}</div>
                                <div className="text-xs text-muted-foreground">{p.category_slug} · {formatINR(p.base_price_inr)}</div>
                                <div className="text-[11px] mt-1">{p.sizes?.length || 0} sizes · {p.is_active ? "Active" : "Hidden"}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const TeamLeads = () => {
    const [leads, setLeads] = useState([]);
    const [scope, setScope] = useState("mine");
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/team/leads", { params: { scope } }); setLeads(data.leads); } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

    const updateStatus = async (lead, status) => {
        try {
            await api.patch(`/team/leads/${lead.id}/status`, { status });
            setLeads((ls) => ls.map((x) => x.id === lead.id ? { ...x, status } : x));
            toast.success("Status updated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="p-6 md:p-8" data-testid="team-leads">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Leads</h1>
                    <p className="text-sm text-muted-foreground">Leads assigned to you by the admin team</p>
                </div>
                <div className="rounded-full glass p-1 flex text-xs">
                    <button onClick={() => setScope("mine")} className={`px-3 py-1.5 rounded-full font-medium ${scope === "mine" ? "bg-primary text-primary-foreground" : "hover:bg-primary/5"}`}>Assigned to me</button>
                    <button onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-full font-medium ${scope === "all" ? "bg-primary text-primary-foreground" : "hover:bg-primary/5"}`}>All</button>
                </div>
            </div>
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mt-6" /> : (
                <div className="mt-6 rounded-2xl bg-card border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left"><tr><th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">City / Occasion</th><th className="p-3">Status</th><th className="p-3">When</th></tr></thead>
                        <tbody>
                            {leads.map((l) => (
                                <tr key={l.id} className="border-t" data-testid={`team-lead-${l.id}`}>
                                    <td className="p-3 font-semibold">{l.name}</td>
                                    <td className="p-3 text-xs">{l.email}<div className="text-muted-foreground">{l.phone}</div></td>
                                    <td className="p-3 text-xs">{l.city || "—"} · {l.occasion || "—"}</td>
                                    <td className="p-3">
                                        <select
                                            value={l.status || "new"}
                                            onChange={(e) => updateStatus(l, e.target.value)}
                                            data-testid={`team-lead-status-${l.id}`}
                                            className={`rounded-full border-0 px-3 h-7 text-[11px] font-semibold uppercase tracking-widest cursor-pointer focus:ring-2 focus:ring-primary ${leadStatusBadgeClass(l.status)}`}
                                        >
                                            {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="p-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                            {leads.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No leads assigned yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
